import { createApi } from "@reduxjs/toolkit/query/react";

import {
  CalendarDaySheet,
  DaySheet,
  GetSingleDaySheetType,
  IAllocatedFleet,
  IFleetBooking,
} from "@/interfaces/roster.interface";
import { TPartialContractJob } from "@/schemas/roster-schema";
import { BaseWithAuth, getWorkspace } from "@/utils/helpers";

export const rosterApi = createApi({
  reducerPath: "rosterApi",
  baseQuery: BaseWithAuth(),
  tagTypes: ["daySheet", "fleet", "allocated", "singleDaySheet"],
  endpoints: (builder) => ({
    //get the day sheet
    getDaySheet: builder.query<
      DaySheet | DaySheet[],
      { daySheetType?: string; filter?: string }
    >({
      query: ({ daySheetType, filter }) => {
        return `/workspace/${getWorkspace()}/roster/day-sheet?${daySheetType ? `daySheetType=${daySheetType}` : ""}${filter ? filter : "&"}`;
      },
      providesTags: ["daySheet"],
      transformResponse: (response: APIResponse<DaySheet | DaySheet[]>) =>
        response?.data,
    }),

    getCalendarDaySheet: builder.query<CalendarDaySheet[], string>({
      query: (dateRange) => {
        return `/workspace/${getWorkspace()}/roster/calendar/${dateRange}`;
      },
      providesTags: ["daySheet", "allocated", "fleet"],
      transformResponse: (response: APIResponse<CalendarDaySheet[]>) =>
        response?.data,
    }),
    getSingleDaySheet: builder.query<GetSingleDaySheetType, { id: string }>({
      query: ({ id }) => {
        return `/workspace/${getWorkspace()}/roster/day-sheet/${id}`;
      },
      providesTags: ["singleDaySheet"],
      transformResponse: (response: APIResponse<GetSingleDaySheetType>) =>
        response?.data,
    }),

    //add the day sheet
    addDaySheet: builder.mutation<unknown, object>({
      query: (data) => ({
        url: `/workspace/${getWorkspace()}/roster/day-sheet`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["daySheet", "fleet"],
    }),

    updateEmpDaySheet: builder.mutation<
      unknown,
      { data: object; daySheetId: string }
    >({
      query: ({ data, daySheetId }) => ({
        url: `/workspace/${getWorkspace()}/roster/day-sheet/${daySheetId}/employee`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["daySheet"],
    }),
    updateJobFleet: builder.mutation<unknown, { daySheetId: string }>({
      query: ({ daySheetId }) => ({
        url: `/workspace/${getWorkspace()}/roster/day-sheet/${daySheetId}/fleet`,
        method: "PATCH",
      }),
      invalidatesTags: ["daySheet", "fleet"],
    }),

    updateJobItems: builder.mutation<
      unknown,
      { data: TPartialContractJob; daySheetId: string }
    >({
      query: ({ data, daySheetId }) => ({
        url: `/workspace/${getWorkspace()}/roster/day-sheet/${daySheetId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["daySheet"],
    }),

    mutateFloatingJob: builder.mutation<
      unknown,
      { data: object; type?: string; method: string }
    >({
      query: ({ data, type, method }) => ({
        url: `/workspace/${getWorkspace()}/roster/day-sheet/${type || ""}`,
        method: method.toUpperCase(),
        body: data,
      }),
      invalidatesTags: ["daySheet"],
    }),

    getFleeBooking: builder.query<
      IFleetBooking | IFleetBooking[],
      { offset?: string }
    >({
      query: ({ offset }) => {
        return `/workspace/${getWorkspace()}/roster/fleets${offset ? offset : ""}`;
      },
      providesTags: ["fleet"],
      transformResponse: (
        response: APIResponse<IFleetBooking | IFleetBooking[]>,
      ) => response?.data,
    }),

    mutateFleet: builder.mutation<
      unknown,
      { data?: object; offset?: string; method: string }
    >({
      query: ({ data, offset, method }) => ({
        url: `/workspace/${getWorkspace()}/roster/fleets/${offset || ""}`,
        method: method.toUpperCase(),
        body: data,
      }),
      invalidatesTags: ["fleet", "allocated"],
    }),

    getAllocatedFleet: builder.query<
      IAllocatedFleet | IAllocatedFleet[],
      { offset?: string }
    >({
      query: ({ offset }) => {
        return `/workspace/${getWorkspace()}/roster/fleets/allocated${offset ? offset : ""}`;
      },
      providesTags: ["allocated"],
      transformResponse: (
        response: APIResponse<IAllocatedFleet | IAllocatedFleet[]>,
      ) => response?.data,
    }),

    mutateAllocatedFleet: builder.mutation<
      unknown,
      { data?: object; offset?: string; method: string }
    >({
      query: ({ data, offset, method }) => ({
        url: `/workspace/${getWorkspace()}/roster/fleets/allocated${offset || ""}`,
        method: method.toUpperCase(),
        body: data,
      }),
      invalidatesTags: ["allocated"],
    }),

    mutateFloatingFleet: builder.mutation<
      unknown,
      { data: object; offset?: string; method: string }
    >({
      query: ({ data, offset, method }) => ({
        url: `/workspace/${getWorkspace()}/roster/fleets/${offset || ""}`,
        method: method.toUpperCase(),
        body: data,
      }),
      invalidatesTags: ["allocated", "fleet"],
    }),
  }),
});

export const {
  useGetCalendarDaySheetQuery,

  useGetDaySheetQuery,
  useAddDaySheetMutation,
  useUpdateEmpDaySheetMutation,
  useUpdateJobItemsMutation,
  useUpdateJobFleetMutation,
  useMutateFloatingJobMutation,
  useGetSingleDaySheetQuery,
  useGetFleeBookingQuery,
  useMutateFleetMutation,
  useGetAllocatedFleetQuery,
  useMutateAllocatedFleetMutation,
  useMutateFloatingFleetMutation,
} = rosterApi;
